/**
 * Tariffs API. Normalizes snake_case from Laravel to camelCase for frontend.
 */
import api from './api';
import { endpoints } from './endpoints';
import { STANDARD_TARIFF_TYPE_KEYS } from '../config/tariffs';

function normalizeTariff(raw) {
  if (!raw) return null;
  const price = raw.monthly_price ?? raw.monthlyPrice ?? 0;
  return {
    id: String(raw.id),
    typeKey: raw.type_key ?? raw.typeKey ?? null,
    customName: raw.custom_name ?? raw.customName ?? null,
    monthlyPrice: typeof price === 'string' ? parseFloat(price) || 0 : Number(price),
    benefits: Array.isArray(raw.benefits) ? raw.benefits : [],
    recommended: Boolean(raw.recommended),
  };
}

function canonicalStandardTariffKey(plan) {
  const key = String(plan?.typeKey ?? '').trim().toLowerCase();
  if (key) return key;

  const custom = String(plan?.customName ?? '').trim().toLowerCase();
  if (custom === 'start') return 'start';
  if (custom === 'businesssmall' || custom === 'small business' || custom === 'kichik biznes') return 'businesssmall';
  if (custom === 'businessmedium' || custom === 'medium business' || custom === "o'rta biznes" || custom === 'o‘rta biznes') return 'businessmedium';
  if (custom === 'businessenterprise' || custom === 'enterprise' || custom === 'yirik korporatsiya') return 'businessenterprise';

  return '';
}

/**
 * Barcha tariflarni qaytaradi. response.data yoki response.data.data massivini map qiladi.
 * @returns {Promise<import('../config/tariffs').TariffPlan[]>}
 */
export async function getTariffs() {
  const res = await api.get(endpoints.tariffs);
  const raw = res?.data;
  const arr = Array.isArray(raw) ? raw : (raw?.data && Array.isArray(raw.data) ? raw.data : []);
  const normalized = arr.map(normalizeTariff).filter(Boolean);
  const standardKeys = new Set(STANDARD_TARIFF_TYPE_KEYS.map((k) => String(k).toLowerCase()));
  const seenStandard = new Set();

  return normalized.filter((plan) => {
    const key = canonicalStandardTariffKey(plan);
    if (key === 'businesspremium') return false;
    if (!standardKeys.has(key)) return true;
    if (seenStandard.has(key)) return false;
    seenStandard.add(key);
    return true;
  });
}

/**
 * @param {{ typeKey?: string | null, customName?: string | null, monthlyPrice: number, benefits: string[], recommended: boolean }} payload
 * @returns {Promise<import('../config/tariffs').TariffPlan>}
 */
export async function createTariff(payload) {
  const body = {
    type_key: payload.typeKey ?? null,
    custom_name: payload.customName ?? null,
    monthly_price: payload.monthlyPrice,
    benefits: payload.benefits ?? [],
    recommended: payload.recommended ?? false,
  };
  const { data } = await api.post(endpoints.tariffs, body);
  const raw = data?.data ?? data;
  return normalizeTariff(raw);
}

/**
 * @param {string} id
 * @param {{ typeKey?: string | null, customName?: string | null, monthlyPrice?: number, benefits?: string[], recommended?: boolean }} payload
 * @returns {Promise<import('../config/tariffs').TariffPlan>}
 */
export async function updateTariff(id, payload) {
  const body = {
    ...(payload.typeKey !== undefined && { type_key: payload.typeKey }),
    ...(payload.customName !== undefined && { custom_name: payload.customName }),
    ...(payload.monthlyPrice !== undefined && { monthly_price: payload.monthlyPrice }),
    ...(payload.benefits !== undefined && { benefits: payload.benefits }),
    ...(payload.recommended !== undefined && { recommended: payload.recommended }),
  };
  const { data } = await api.put(endpoints.tariffById(id), body);
  const raw = data?.data ?? data;
  return normalizeTariff(raw);
}

/**
 * @param {string} id
 * @returns {Promise<void>}
 */
export async function deleteTariff(id) {
  await api.delete(endpoints.tariffById(id));
}
